import { useState } from "react";
import { Input } from "./ui/input";
import { yen } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Search } from "lucide-react";
import { uniqueCats, sameCat } from "../lib/cats";

export const ProductGrid = ({ products, onPick, onBarcode }) => {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("__all__");
  const cats = ["__all__", ...uniqueCats(products.map((p) => p.category))];
  const list = products.filter(
    (p) =>
      (cat === "__all__" || sameCat(p.category, cat)) &&
      (p.name.toLowerCase().includes(q.toLowerCase()) ||
        (p.barcode || "").includes(q))
  );

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          data-testid="product-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && q.trim()) {
              if (onBarcode) onBarcode(q.trim(), () => setQ(""));
              else if (list.length > 0) {
                onPick(list[0]);
                setQ("");
              }
            }
          }}
          placeholder={t("search_placeholder")}
          className="h-12 pl-10 rounded-xl bg-white"
        />
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {cats.map((c) => (
          <button
            key={c}
            data-testid={`category-${c === "__all__" ? "Semua" : c}`}
            onClick={() => setCat(c)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
              cat === c
                ? "bg-gray-900 text-white"
                : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {c === "__all__" ? t("all") : c}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
        {list.map((p) => {
          const low = p.track_stock && p.stock <= p.low_stock_threshold;
          const out = p.track_stock && p.stock <= 0;
          return (
            <button
              key={p.id}
              data-testid={`product-card-${p.id}`}
              onClick={() => onPick(p)}
              className={`text-left bg-white border shadow-sm rounded-xl overflow-hidden transition-all duration-200 ${
                out
                  ? "opacity-50 border-red-200 cursor-not-allowed"
                  : "border-gray-100 hover:-translate-y-1 hover:shadow-md active:scale-95"
              }`}
            >
              <div className="aspect-[4/3] bg-gray-100 overflow-hidden relative">
                {out && (
                  <span
                    data-testid={`product-out-badge-${p.id}`}
                    className="absolute top-2 left-2 z-10 text-[10px] font-bold tracking-wide text-white bg-red-600 rounded-full px-2 py-1 shadow"
                  >
                    HABIS
                  </span>
                )}
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt={p.name}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full grid place-items-center text-gray-300 text-xs">
                    {p.category}
                  </div>
                )}
              </div>
              <div className="p-4">
                <p className="text-xs text-muted-foreground">{p.category}</p>
                <p className="font-medium leading-snug mt-1 line-clamp-2 min-h-[42px]">
                  {p.name}
                </p>
                <div className="flex items-end justify-between mt-2">
                  <span className="num text-lg font-semibold">{yen(p.price)}</span>
                  {p.track_stock && (
                    <span
                      className={`text-xs num px-2 py-0.5 rounded-full ${
                        out
                          ? "bg-red-50 text-red-600"
                          : low
                          ? "bg-amber-50 text-amber-600"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {out ? t("out_of_stock") : `${p.stock} ${p.unit}`}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
        {list.length === 0 && (
          <p className="text-muted-foreground col-span-full py-8 text-center text-sm">
            {t("no_data")}
          </p>
        )}
      </div>
    </div>
  );
};
